// src/utils/answerCheck.js
//
// Is one answer right? Shared by the results review and the results insights so
// both always agree with each other, and with examService.calculateFinalScore.

import { isTableGapQuestion, isTableGapCorrect } from './tableAnswers';
import { isNotesPart, isNoteCorrect } from './notesAnswers';
import { isSpellingLenientPart, isGapAnswerLenient } from './gapAnswers';

// Task/question types whose answers are richtig/falsch (or ja/nein) verdicts
// instead of references to a list of options.
export const VERDICT_TYPE_PATTERN = /true-false|richtig|yes-no|ja-nein/;

// A part can mix item types (telc "mixed-true-false-mc"), so the question's own
// type always wins over the part's.
export const isVerdictQuestion = (question, part) => {
  const ownType = String(question?.questionType || '').toLowerCase();
  if (ownType) return VERDICT_TYPE_PATTERN.test(ownType);
  const partType = `${part?.questionType || ''} ${part?.taskType || ''}`.toLowerCase();
  return VERDICT_TYPE_PATTERN.test(partType);
};

export const isMatchingQuestion = (question, part) => {
  const ownType = String(question?.questionType || '').toLowerCase();
  if (ownType) return ownType.includes('matching');
  return String(part?.questionType || '').toLowerCase().includes('matching');
};

// Comparison mirrors the review's answer labels: the richtig/falsch aliases are
// only collapsed for verdict items, so a matching answer of "f" is never treated
// as "falsch". Kept deliberately in step with examService.calculateFinalScore so
// the review screen can never disagree with the score.
export const isAnswerCorrect = (userAnswer, question, part) => {
  if (isTableGapQuestion(question)) return isTableGapCorrect(question, userAnswer);
  if (isNotesPart(part)) return userAnswer != null && isNoteCorrect(question.correctAnswer, userAnswer);
  if (isSpellingLenientPart(part)) return userAnswer != null && isGapAnswerLenient(question.correctAnswer, userAnswer);
  const normalize = (answer) => {
    if (typeof answer === 'boolean') return answer ? 'richtig' : 'falsch';
    const raw = typeof answer === 'string' ? answer.trim() : String(answer);
    const lower = raw.toLowerCase();
    if (!isVerdictQuestion(question, part)) return lower;
    if (lower === 'true' || lower === 'r' || raw === '+') return 'richtig';
    if (lower === 'false' || lower === 'f' || raw === '-') return 'falsch';
    if (lower === 'yes') return 'ja';
    if (lower === 'no') return 'nein';
    return lower;
  };

  // A list means several accepted answers (e.g. ÖSD Sprachbausteine "bei" / "auf").
  const accepted = Array.isArray(question?.correctAnswer) ? question.correctAnswer : [question?.correctAnswer];
  return accepted.some((answer) => normalize(userAnswer) === normalize(answer));
};
